import { useState } from 'react';
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { Eyebrow, Reveal } from './Reveal';

const FIELDS = [
  { id: 'company-name', label: 'Company name *', type: 'text', testid: 'form-company-name', autoComplete: 'organization' },
  { id: 'contact-person', label: 'Contact person *', type: 'text', testid: 'form-contact-person', autoComplete: 'name' },
  { id: 'contact-number', label: 'Contact number *', type: 'tel', testid: 'form-contact-number', autoComplete: 'tel' },
  { id: 'location', label: 'Location *', type: 'text', testid: 'form-location', autoComplete: 'address-level2' },
];

const inputCls =
  'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-400 shadow-sm transition-[border-color,box-shadow] duration-200 focus:border-royal focus:outline-none focus:ring-4 focus:ring-royal/15';

export default function Contact() {
  const [status, setStatus] = useState('idle');

  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const payload = {
      company_name: f.get('company-name'),
      contact_person: f.get('contact-person'),
      contact_number: f.get('contact-number'),
      location: f.get('location'),
      service_required: f.get('service-required'),
      requirement: f.get('workforce-requirement'),
    };
    setStatus('sending');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('send failed');
      setStatus('sent');
      form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-28 bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow index="10">Let&apos;s work together</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Tell us your <span className="text-royal">workforce requirement.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Need employees, temporary staff or managed payroll support? Share a few details and our team can
              discuss the next steps with you.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Contact info */}
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="grain-dark relative h-full overflow-hidden rounded-[1.75rem] bg-royal-ink p-8 sm:p-10">
              <div aria-hidden="true" className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-royal/60 blur-3xl" />
              <h3 className="relative font-display text-xl font-extrabold text-white">Speak with Sania Job Bowl</h3>
              <ul className="relative mt-8 space-y-6">
                <li className="flex items-start gap-4" data-testid="contact-phone">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-lime">
                    <Phone className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-white/50">Phone</p>
                    <a href="tel:+916361186321" className="mt-1 block font-display text-lg font-bold text-white transition-colors duration-200 hover:text-lime">
                      +91 63611 86321
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-email">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-lime">
                    <Mail className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-white/50">Email</p>
                    <a href="mailto:connect@saniajobbowl.com" className="mt-1 block break-all font-display text-lg font-bold text-white transition-colors duration-200 hover:text-lime">
                      connect@saniajobbowl.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-office">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-lime">
                    <MapPin className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-white/50">Registered office</p>
                    <p className="mt-1 text-sm leading-relaxed text-white/80">
                      374, 1st A Cross, 1st Main Road, 7th Block, Koramangala, Bengaluru – 560095, Karnataka, India.
                    </p>
                  </div>
                </li>
              </ul>
              <p className="relative mt-8 border-t border-white/10 pt-6 text-sm leading-relaxed text-white/60">
                Prefer WhatsApp? Use the green contact button to start a conversation with our team.
              </p>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.2} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="h-full rounded-[1.75rem] border border-slate-200/80 bg-white p-8 shadow-card sm:p-10"
            >
              <h3 className="font-display text-xl font-extrabold text-ink">Employer enquiry</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                Complete this form and our team will receive your requirement by email. No information is stored on
                this website.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <div key={f.id}>
                    <label htmlFor={f.id} className="mb-1.5 block font-display text-[13px] font-bold text-ink">
                      {f.label}
                    </label>
                    <input id={f.id} name={f.id} type={f.type} required autoComplete={f.autoComplete} data-testid={f.testid} className={inputCls} />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label htmlFor="service-required" className="mb-1.5 block font-display text-[13px] font-bold text-ink">
                    Service required *
                  </label>
                  <select id="service-required" name="service-required" required data-testid="form-service-required" className={inputCls} defaultValue="Recruitment">
                    <option value="Recruitment">Recruitment — Sania Talent Solutions</option>
                    <option value="Flexible Staffing">Flexible Staffing — Sania One Day Hiring</option>
                    <option value="Payroll">Payroll — Sania Payroll Solutions</option>
                    <option value="Other">Other business enquiry</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="workforce-requirement" className="mb-1.5 block font-display text-[13px] font-bold text-ink">
                    Workforce requirement *
                  </label>
                  <textarea id="workforce-requirement" name="workforce-requirement" required rows={5} data-testid="form-workforce-requirement" className={`${inputCls} resize-y`} />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                data-testid="form-submit-button"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-royal px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_16px_32px_-12px_rgba(45,58,151,0.65)] transition-[transform,background-color,box-shadow,opacity] duration-200 hover:-translate-y-0.5 hover:bg-royal-dark disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status === 'sending' ? (
                  <>
                    Sending
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    Send Enquiry →
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </>
                )}
              </button>

              {status === 'sent' && (
                <div data-testid="form-success-message" role="status" className="mt-5 rounded-2xl border border-lime/60 bg-lime-soft px-5 py-4 text-sm font-semibold text-ink">
                  Thank you — your enquiry has been emailed to our team at connect@saniajobbowl.com.
                </div>
              )}
              {status === 'error' && (
                <div data-testid="form-error-message" role="alert" className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
                  We couldn&apos;t send your enquiry just now. Please email us directly at connect@saniajobbowl.com or
                  call +91 63611 86321.
                </div>
              )}
              <p className="mt-5 text-xs leading-relaxed text-slate-500" data-testid="form-note">
                Your enquiry goes straight to our team&apos;s inbox. We do not store your details on this website.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
